import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { Link } from 'react-router-dom';

import type { ILineItem } from '@feathr/blackbox';
import type { IColumn } from '@feathr/components';
import { Button, TableColumnHeader } from '@feathr/components';
import { useLocalUrl } from '@feathr/extender/state';
import { currencyFormatter } from '@feathr/extender/utils';

import * as tableStyles from '@feathr/components/dist/Table/Table.css';

/**
 * Default (customer) campaign link: prefixes the route with the current account via useLocalUrl.
 * Rendered only when no `buildLineItemUrl` is supplied, so the account-context hook is never
 * invoked on the Perch path (which supplies `buildLineItemUrl`).
 */
const CampaignLink = observer(function CampaignLink({
  campaignId,
  description,
  eventId,
}: Readonly<{ campaignId: string; description: string; eventId: string }>): JSX.Element {
  const localUrl = useLocalUrl();
  return <Link to={localUrl(`/projects/${eventId}/campaigns/${campaignId}`)}>{description}</Link>;
});

function BillColumns(
  data: ILineItem[],
  eventId: string,
  buildLineItemUrl?: (eventId: string, campaignId: string) => string,
): Array<IColumn<ILineItem>> {
  return [
    {
      id: 'description',
      Header: TableColumnHeader({
        sortType: 'alpha',
        title: 'Campaign',
      }),
      headerClassName: tableStyles.sort,
      className: tableStyles.cell,
      Cell({ original }): JSX.Element {
        if (!original.campaign_id) {
          return <>{original.description}</>;
        }
        if (buildLineItemUrl) {
          return (
            <Button
              href={buildLineItemUrl(eventId, original.campaign_id)}
              target={'_blank'}
              type={'link'}
            >
              {original.description}
            </Button>
          );
        }
        return (
          <CampaignLink
            campaignId={original.campaign_id}
            description={original.description}
            eventId={eventId}
          />
        );
      },
    },
    {
      id: 'quantity',
      Header: TableColumnHeader({
        sortType: 'numeric',
        title: 'Qty',
      }),
      width: 80,
      headerClassName: tableStyles.sort,
      className: tableStyles.cellRight,
      Cell: ({ original }): JSX.Element => {
        return <>{original.quantity}</>;
      },
    },
    {
      id: 'unitPrice',
      Header: TableColumnHeader({
        sortType: 'numeric',
        title: 'Unit Price',
      }),
      width: 120,
      headerClassName: tableStyles.sort,
      className: tableStyles.cellRight,
      Cell: ({ original }): JSX.Element => {
        const unitPrice = original.price;
        return <>{currencyFormatter.format(unitPrice)}</>;
      },
    },
    {
      id: 'price',
      Header: TableColumnHeader({
        sortType: 'numeric',
        title: 'Total',
      }),
      width: 200,
      headerClassName: tableStyles.sort,
      className: tableStyles.cellRight,
      Cell: ({ original }): JSX.Element => {
        const total = original.price * original.quantity;
        return <>{currencyFormatter.format(total)}</>;
      },
      Footer(): JSX.Element {
        return (
          <>
            <strong>Amount Due:</strong>{' '}
            {currencyFormatter.format(
              data.reduce((total, item) => total + item.price * item.quantity, 0),
            )}
          </>
        );
      },
    },
  ];
}

export default BillColumns;
